import React, { useState, useEffect } from 'react';

// ── Dashboard da Revista (página dedicada) ──────────────────────────────────
// Densidade de processos por estado em qualquer RPI importada, com filtros de
// despacho (todos os tipos da revista + 7 gatilhos comerciais agrupados) e de
// procurador (todos / com / sem / escritório específico).

const apiFetch = (path, method = 'GET', body) =>
  fetch(`/${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => r.json());

// ── Dashboard da Revista: gatilhos × estado com filtro de procurador ────────
const UF_GRID = { // mapa do Brasil em ladrilhos (col, linha)
  RR: [2, 0], AP: [4, 0],
  AM: [1, 1], PA: [3, 1], MA: [4, 1], CE: [5, 1], RN: [6, 1],
  AC: [0, 2], RO: [1, 2], MT: [2, 2], TO: [3, 2], PI: [4, 2], PE: [5, 2], PB: [6, 2],
  MS: [2, 3], GO: [3, 3], DF: [4, 3], BA: [5, 3], AL: [6, 3],
  SP: [3, 4], MG: [4, 4], ES: [5, 4], SE: [6, 4],
  PR: [3, 5], RJ: [4, 5],
  SC: [3, 6],
  RS: [3, 7],
};
const UF_NOME = { AC: 'Acre', AL: 'Alagoas', AM: 'Amazonas', AP: 'Amapá', BA: 'Bahia', CE: 'Ceará', DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás', MA: 'Maranhão', MG: 'Minas Gerais', MS: 'Mato Grosso do Sul', MT: 'Mato Grosso', PA: 'Pará', PB: 'Paraíba', PE: 'Pernambuco', PI: 'Piauí', PR: 'Paraná', RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte', RO: 'Rondônia', RR: 'Roraima', RS: 'Rio Grande do Sul', SC: 'Santa Catarina', SE: 'Sergipe', SP: 'São Paulo', TO: 'Tocantins' };
// Reusados pelo Mapa da Carteira do Sucesso do Cliente
export { UF_GRID, UF_NOME };
const ROTULO_GATILHO = { sozinhos: 'Sozinhos', exigencia: 'Exigência', indeferido: 'Indeferido', oposicao: 'Oposição', deferido: 'Deferido', expirado: 'Expirado', perdido: 'Perdido' };

// Classes de Nice (NCL) com apelidos curtos — o segmento de mercado da marca
const CLASSES_NCL = {
  1: 'Químicos p/ indústria', 2: 'Tintas e vernizes', 3: 'Cosméticos e limpeza', 4: 'Óleos e combustíveis',
  5: 'Farmacêuticos', 6: 'Metais e serralheria', 7: 'Máquinas', 8: 'Ferramentas manuais',
  9: 'Eletrônicos e software', 10: 'Aparelhos médicos', 11: 'Iluminação e sanitários', 12: 'Veículos',
  13: 'Armas e explosivos', 14: 'Joias e relógios', 15: 'Instrumentos musicais', 16: 'Papelaria e impressos',
  17: 'Borracha e plásticos', 18: 'Couro, bolsas e malas', 19: 'Materiais de construção', 20: 'Móveis',
  21: 'Utensílios domésticos', 22: 'Cordas e lonas', 23: 'Fios têxteis', 24: 'Tecidos e cama/mesa/banho',
  25: 'Vestuário e calçados', 26: 'Aviamentos', 27: 'Tapetes e revestimentos', 28: 'Jogos e brinquedos',
  29: 'Alimentos (carnes e laticínios)', 30: 'Alimentos (café, massas e pães)', 31: 'Agrícolas e animais',
  32: 'Bebidas não alcoólicas e cervejas', 33: 'Bebidas alcoólicas', 34: 'Fumo e artigos',
  35: 'Publicidade, gestão e varejo', 36: 'Financeiro e imobiliário', 37: 'Construção e reparos',
  38: 'Telecomunicações', 39: 'Transporte e logística', 40: 'Tratamento de materiais',
  41: 'Educação e entretenimento', 42: 'Tecnologia e software (serviços)', 43: 'Alimentação e hospedagem',
  44: 'Saúde, beleza e estética', 45: 'Jurídicos e segurança',
};
const SeletorClasse = ({ valor, onChange, estilo }) => (
  <select value={valor} onChange={e => onChange(e.target.value)} style={estilo}>
    <option value="">Todas as classes (NCL)</option>
    {Object.entries(CLASSES_NCL).map(([n, rot]) => (
      <option key={n} value={n}>NCL {String(n).padStart(2, '0')} — {rot}</option>
    ))}
  </select>
);

// Escala de temperatura (claro → escuro conforme a densidade)
function corCalor(n, max) {
  if (!n) return { bg: '#f3f4f6', texto: '#9ca3af' };
  const r = n / (max || 1);
  if (r < 0.15) return { bg: '#fee2e2', texto: '#7f1d1d' };
  if (r < 0.35) return { bg: '#fca5a5', texto: '#7f1d1d' };
  if (r < 0.6) return { bg: '#ef4444', texto: '#fff' };
  if (r < 0.85) return { bg: '#b91c1c', texto: '#fff' };
  return { bg: '#7f1d1d', texto: '#fff' };
}

// Exporta CSV que o Excel pt-BR abre direto (BOM + ponto e vírgula)
function baixarCsv(nome, cabecalho, linhas) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = '\ufeff' + [cabecalho, ...linhas].map(l => l.map(esc).join(';')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nome;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const podeGerarLista = () =>
  localStorage.getItem('nomeUsuario') === 'Vagner Freitas' ||
  (localStorage.getItem('cargoUsuario') || '').toLowerCase().includes('closer');

// ── Modal: processos do estado clicado, respeitando os filtros ativos ───────
// ── Estatísticas do estado na revista: procuradores × movimentações e
// ranking dos tipos de despacho (revista INTEIRA do estado, sem recorte)
function ModalEstatisticasUf({ uf, rpi, onFechar }) {
  const [dados, setDados] = useState(null);
  useEffect(() => {
    apiFetch(`marcas-rpi/dashboard-revista/estatisticas-estado?rpi=${encodeURIComponent(rpi)}&uf=${encodeURIComponent(uf)}`)
      .then(d => setDados(d.erro ? { erro: d.erro } : d))
      .catch(() => setDados({ erro: 'Falha ao buscar as estatísticas' }));
  }, [uf, rpi]);

  const Bloco = ({ titulo, linhas, col1 }) => {
    const total = linhas.reduce((s, l) => s + l.qtd, 0) || 1;
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1628', margin: '0 0 6px' }}>{titulo}</div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, maxHeight: 260, overflowY: 'auto' }}>
          {linhas.map((l, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderBottom: i < linhas.length - 1 ? '1px solid #f3f4f6' : 'none', background: i % 2 ? '#fafafa' : '#fff' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', width: 26, textAlign: 'right' }}>{i + 1}º</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: l.nome === '(sem procurador)' ? 800 : 600, color: l.nome === '(sem procurador)' ? '#b45309' : '#0a1628', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col1(l)}</div>
                {l.sub && <div style={{ fontSize: 10.5, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.sub}</div>}
              </div>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: '#0a1628' }}>{l.qtd.toLocaleString('pt-BR')}</span>
              <span style={{ fontSize: 11, color: '#6b7280', width: 38, textAlign: 'right' }}>{Math.round(l.qtd / total * 100)}%</span>
            </div>
          ))}
          {linhas.length === 0 && <div style={{ padding: 12, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>Nada encontrado.</div>}
        </div>
      </div>
    );
  };

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1010, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: 600, maxWidth: '96%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
        <div style={{ background: '#0a1628', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📊 Estatísticas — {UF_NOME[uf] || uf} · RPI {rpi}</span>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
              Revista inteira do estado{dados && !dados.erro ? ` · ${(dados.movimentacoes || 0).toLocaleString('pt-BR')} movimentações` : ''}
            </div>
          </div>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '12px 14px' }}>
          {!dados ? (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: 24, fontSize: 13 }}>Calculando…</div>
          ) : dados.erro ? (
            <div style={{ textAlign: 'center', color: '#dc2626', padding: 24, fontSize: 13 }}>⚠ {String(dados.erro)}</div>
          ) : (
            <>
              <Bloco titulo={`🏆 Tipos de movimentação (${(dados.despachos || []).length})`}
                linhas={(dados.despachos || []).map(d => ({ nome: d.codigo, sub: d.nome, qtd: d.qtd }))}
                col1={(l) => l.nome} />
              <Bloco titulo={`👨‍⚖️ Procuradores (${(dados.procuradores || []).length})`}
                linhas={dados.procuradores || []}
                col1={(l) => l.nome} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalProcessosUf({ uf, rpi, procurador, despacho, gatilho, classe, rotuloFiltro, onFechar }) {
  const [dados, setDados] = useState(null);
  const [busca, setBusca] = useState('');
  const [copiado, setCopiado] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [listaOk, setListaOk] = useState(null);
  const [mostrarStats, setMostrarStats] = useState(false);

  async function gerarListaSdr() {
    setGerando(true);
    try {
      const r = await apiFetch('sdr/radar/gerar-estado', 'POST', { rpi, uf, gatilho: gatilho || '', despacho: despacho || '', classe: classe || '', motivo: rotuloFiltro });
      setListaOk(r.erro ? { erro: r.erro } : r);
    } catch { setListaOk({ erro: 'Falha ao gerar a lista' }); }
    setGerando(false);
  }

  useEffect(() => {
    const q = [
      `rpi=${encodeURIComponent(rpi)}`,
      `uf=${encodeURIComponent(uf)}`,
      `procurador=${encodeURIComponent(procurador)}`,
      despacho ? `despacho=${encodeURIComponent(despacho)}` : '',
      gatilho ? `gatilho=${encodeURIComponent(gatilho)}` : '',
      classe ? `classe=${encodeURIComponent(classe)}` : '',
    ].filter(Boolean).join('&');
    apiFetch(`marcas-rpi/dashboard-revista/processos?${q}`)
      .then(d => setDados(d.erro ? { erro: d.erro } : d))
      .catch(() => setDados({ erro: 'Falha ao buscar os processos' }));
  }, [uf, rpi, procurador, despacho, gatilho, classe]);

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onFechar]);

  function copiar(numero) {
    const fallback = () => {
      const el = document.createElement('textarea');
      el.value = numero; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    };
    if (navigator.clipboard) navigator.clipboard.writeText(numero).catch(fallback); else fallback();
    setCopiado(numero);
    setTimeout(() => setCopiado(null), 1500);
  }

  const processos = (dados?.processos || []).filter(p => {
    if (!busca) return true;
    const b = busca.toLowerCase();
    const dig = busca.replace(/\D/g, '');
    return (p.marca || '').toLowerCase().includes(b) || (p.titular || '').toLowerCase().includes(b)
      || (dig.length >= 3 && String(p.numero).includes(dig));
  });

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      {mostrarStats && <ModalEstatisticasUf uf={uf} rpi={rpi} onFechar={() => setMostrarStats(false)} />}
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: 640, maxWidth: '96%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        <div style={{ background: '#0a1628', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📍 {UF_NOME[uf] || uf} — RPI {rpi}</span>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
              {rotuloFiltro}{dados && !dados.erro ? ` · ${(dados.total || 0).toLocaleString('pt-BR')} processo(s)` : ''}
            </div>
          </div>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '8px 14px', borderBottom: '1px solid #e8eaed' }}>
          <input value={busca} onChange={e => setBusca(e.target.value)} autoFocus
            placeholder="Filtrar por marca, titular ou nº do processo…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 13 }} />
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {!dados ? (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: 24, fontSize: 13 }}>Buscando processos…</div>
          ) : dados.erro ? (
            <div style={{ textAlign: 'center', color: '#dc2626', padding: 24, fontSize: 13 }}>⚠ {String(dados.erro)}</div>
          ) : processos.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 24, fontSize: 13 }}>Nenhum processo{busca ? ' nessa busca' : ' neste filtro'}.</div>
          ) : processos.map((p, i) => (
            <div key={`${p.numero}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 14px', borderBottom: '1px solid #f3f4f6', background: i % 2 ? '#fafafa' : '#fff' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0a1628', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.marca || <em style={{ color: '#9ca3af', fontWeight: 400 }}>(sem nome de marca)</em>}
                </div>
                {p.titular && <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.titular}</div>}
              </div>
              <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>{p.numero}</span>
              <button onClick={() => copiar(String(p.numero))} title="Copiar nº do processo"
                style={{ background: copiado === String(p.numero) ? '#dcfce7' : '#f3f4f6', color: copiado === String(p.numero) ? '#15803d' : '#374151', border: '1px solid #d1d5db', borderRadius: 3, padding: '1px 6px', fontSize: 10.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {copiado === String(p.numero) ? '✓' : '📋'}
              </button>
            </div>
          ))}
          {dados && !dados.erro && (dados.total || 0) > (dados.processos || []).length && (
            <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 11, padding: 8 }}>
              Mostrando os primeiros {(dados.processos || []).length.toLocaleString('pt-BR')} de {dados.total.toLocaleString('pt-BR')} — use a busca acima pra refinar.
            </div>
          )}
        </div>
        {dados && !dados.erro && (
          <div style={{ borderTop: '1px solid #e8eaed', padding: '8px 14px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {podeGerarLista() && (listaOk?.ok ? (
              <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>✓ Lista criada no SDR: {listaOk.inseridos} lead(s)</span>
            ) : (
              <button onClick={gerarListaSdr} disabled={gerando}
                style={{ padding: '4px 12px', background: gerando ? '#e5e7eb' : '#0a1628', color: gerando ? '#9ca3af' : '#fff', border: 'none', borderRadius: 4, fontWeight: 600, fontSize: 12, cursor: gerando ? 'default' : 'pointer' }}>
                {gerando ? 'Gerando…' : '🎯 Virar lista SDR (sem procurador)'}
              </button>
            ))}
            {listaOk?.erro && <span style={{ fontSize: 12, color: '#dc2626' }}>⚠ {listaOk.erro}</span>}
            <button onClick={() => setMostrarStats(true)} title="Procuradores do estado nesta revista + ranking dos tipos de movimentação"
              style={{ padding: '4px 12px', background: '#fff', color: '#0369a1', border: '1px solid #7dd3fc', borderRadius: 4, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
              📊 Estatísticas
            </button>
            <button onClick={() => baixarCsv(`processos_${uf}_rpi${rpi}.csv`, ['Marca', 'Titular', 'Processo', 'Procurador'],
              (dados.processos || []).map(p => [p.marca || '', p.titular || '', p.numero, p.procurador || '']))}
              style={{ marginLeft: 'auto', padding: '4px 12px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
              📥 Exportar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ConteudoDashboard() {
  const [dados, setDados] = useState(null);
  const [rpiSel, setRpiSel] = useState('');
  // Dimensão do mapa: '' = tudo · 'g:key' = gatilho agrupado · 'd:COD' = despacho individual
  const [dimSel, setDimSel] = useState('');
  const [procSel, setProcSel] = useState('__todos__');
  const [ufModal, setUfModal] = useState(null);
  const [modo, setModo] = useState('vol'); // vol = volume · pct = % sem procurador
  const [classeSel, setClasseSel] = useState('');
  const [resumoEnv, setResumoEnv] = useState('');
  const despachoSel = dimSel.startsWith('d:') ? dimSel.slice(2) : '';
  const gatilhoSel = dimSel.startsWith('g:') ? dimSel.slice(2) : '';

  useEffect(() => {
    setDados(null);
    const q = [
      rpiSel ? `rpi=${encodeURIComponent(rpiSel)}` : '',
      `procurador=${encodeURIComponent(procSel)}`,
      despachoSel ? `despacho=${encodeURIComponent(despachoSel)}` : '',
      gatilhoSel ? `gatilho=${encodeURIComponent(gatilhoSel)}` : '',
      classeSel ? `classe=${encodeURIComponent(classeSel)}` : '',
    ].filter(Boolean).join('&');
    apiFetch(`marcas-rpi/dashboard-revista?${q}`)
      .then(d => setDados(d.erro ? { erro: d.erro } : d))
      .catch(() => setDados({ erro: 'Falha ao montar o dashboard' }));
  }, [rpiSel, procSel, despachoSel, gatilhoSel, classeSel]);

  async function testarResumo() {
    setResumoEnv('enviando');
    try {
      const r = await apiFetch('marcas-rpi/dashboard-revista/testar-resumo', 'POST');
      setResumoEnv(r.ok ? `✓ enviado pro ${r.telefone}` : `⚠ ${r.erro || 'falhou'}`);
    } catch { setResumoEnv('⚠ falhou'); }
    setTimeout(() => setResumoEnv(''), 6000);
  }

  const linhas = dados?.linhas || [];
  const porUf = Object.fromEntries(linhas.map(l => [l.uf, l]));
  // Mapa do despacho individual vem pronto do servidor
  const mapaDesp = despachoSel && dados?.mapa ? Object.fromEntries(dados.mapa.map(m => [m.uf, m.n])) : null;
  // % de desassistidos por UF (dimensão atual, independe do filtro de procurador)
  const desass = Object.fromEntries((dados?.desassistidos || []).map(d => [d.uf, d]));
  const valorUf = (uf) => {
    if (modo === 'pct') {
      const e = desass[uf];
      return e && e.total ? Math.round((e.sem / e.total) * 100) : 0;
    }
    if (mapaDesp) return mapaDesp[uf] || 0;
    const l = porUf[uf];
    if (!l) return 0;
    return gatilhoSel ? (l[gatilhoSel] || 0) : l.total;
  };
  const maxUf = Math.max(1, ...Object.keys(UF_GRID).map(valorUf));
  const semUf = valorUf('—');
  const cats = dados?.categorias || [];
  const totais = {};
  cats.forEach(c => { totais[c.key] = linhas.reduce((s, l) => s + (l[c.key] || 0), 0); });
  const totalGeral = linhas.reduce((s, l) => s + l.total, 0);
  const despachoInfo = despachoSel ? (dados?.despachos || []).find(d => d.codigo === despachoSel) : null;
  const totalFiltro = mapaDesp
    ? Object.values(mapaDesp).reduce((s, n) => s + n, 0)
    : gatilhoSel ? (totais[gatilhoSel] || 0) : totalGeral;
  const rotuloBase = despachoInfo
    ? `${despachoInfo.codigo} — ${despachoInfo.nome}`
    : gatilhoSel ? (cats.find(c => c.key === gatilhoSel)?.titulo || '') : 'Todos os despachos';
  const rotuloFiltro = classeSel ? `${rotuloBase} · NCL ${String(classeSel).padStart(2, '0')} ${CLASSES_NCL[classeSel] || ''}` : rotuloBase;
  // Brasil em % de desassistidos (soma dos estados na dimensão atual)
  const desTotais = (dados?.desassistidos || []).reduce((a, d) => ({ total: a.total + d.total, sem: a.sem + d.sem }), { total: 0, sem: 0 });
  const pctBrasil = desTotais.total ? Math.round((desTotais.sem / desTotais.total) * 100) : 0;
  const inpMini = { padding: '3px 6px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 12.5, background: '#fff', height: 26 };
  const thD = { padding: '4px 8px', fontSize: 10.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', background: '#0a1628', whiteSpace: 'nowrap', textAlign: 'center' };
  const tdD = { padding: '3px 8px', fontSize: 12, color: '#374151', borderBottom: '1px solid #e8eaed', textAlign: 'center', whiteSpace: 'nowrap' };

  return (
    <div style={{ background: '#fff', border: '1px solid #c8d0da', borderRadius: 4, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ background: '#e8edf2', borderLeft: '3px solid #0a1628', padding: '3px 10px' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0a1628', textTransform: 'uppercase', letterSpacing: '0.04em' }}>📊 Dashboard da Revista</span>
        <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 10 }}>Densidade de processos por estado — filtre por RPI, gatilho e procurador.</span>
      </div>

      <div style={{ padding: '10px 12px' }}>
        {/* Filtros */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>RPI</div>
            <select value={rpiSel || dados?.rpi || ''} onChange={e => setRpiSel(e.target.value)} style={{ ...inpMini, minWidth: 160, fontWeight: 600 }}>
              {(dados?.rpis || (dados?.rpi ? [{ numero: dados.rpi, data: dados.data }] : [])).map(r => (
                <option key={r.numero} value={r.numero}>RPI {r.numero}{r.data ? ` — ${r.data}` : ''}{String(r.numero) === String(dados?.ultima) ? ' (última)' : ''}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>Despacho / Gatilho</div>
            <select value={dimSel} onChange={e => setDimSel(e.target.value)} style={{ ...inpMini, width: '100%' }}>
              <option value="">Todos os despachos</option>
              <optgroup label="🎯 Gatilhos comerciais (agrupados)">
                {cats.map(c => <option key={c.key} value={'g:' + c.key}>{c.titulo}</option>)}
              </optgroup>
              <optgroup label={`📜 Despachos da revista (${(dados?.despachos || []).length})`}>
                {(dados?.despachos || []).map(d => (
                  <option key={d.codigo} value={'d:' + d.codigo}>
                    {d.codigo} — {d.nome.length > 55 ? d.nome.slice(0, 55) + '…' : d.nome} ({d.n.toLocaleString('pt-BR')})
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>Procurador</div>
            <select value={procSel} onChange={e => setProcSel(e.target.value)} style={{ ...inpMini, width: '100%' }}>
              <option value="__todos__">Todos (com e sem procurador)</option>
              <option value="__com__">✅ Com procurador (assistidos)</option>
              <option value="__sem__">🚫 Sem procurador (desassistidos)</option>
              <optgroup label={`📋 Escritórios da revista (${(dados?.procuradores || []).length.toLocaleString('pt-BR')}) — digite pra pular`}>
                {(dados?.procuradores || []).map(p => (
                  <option key={p.nome} value={p.nome}>{p.nome.length > 45 ? p.nome.slice(0, 45) + '…' : p.nome} ({p.n})</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>Classe NCL (segmento)</div>
            <SeletorClasse valor={classeSel} onChange={setClasseSel} estilo={{ ...inpMini, width: '100%' }} />
          </div>
        </div>

        {/* Modo do mapa + ações */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ display: 'flex', border: '1px solid #b0b8c4', borderRadius: 4, overflow: 'hidden' }}>
            {[['vol', '🌡️ Volume'], ['pct', '🚫 % sem procurador']].map(([m, rot]) => (
              <button key={m} onClick={() => setModo(m)}
                style={{ padding: '4px 10px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: modo === m ? '#0a1628' : '#fff', color: modo === m ? '#fff' : '#374151' }}>
                {rot}
              </button>
            ))}
          </div>
          {modo === 'pct' && <span style={{ fontSize: 11, color: '#6b7280' }}>% de processos desassistidos por estado no filtro de despacho atual — clique no estado pra ver quem são.</span>}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            {resumoEnv && <span style={{ fontSize: 11, color: resumoEnv.startsWith('✓') ? '#15803d' : '#6b7280' }}>{resumoEnv}</span>}
            {localStorage.getItem('nomeUsuario') === 'Vagner Freitas' && (
              <button onClick={testarResumo} disabled={resumoEnv === 'enviando'} title="Envia agora o resumo executivo (o mesmo que chega toda terça com a revista nova) pro seu celular"
                style={{ padding: '4px 10px', fontSize: 12, fontWeight: 600, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>
                📲 Resumo no celular
              </button>
            )}
            <button onClick={() => baixarCsv(`dashboard_rpi${rpiSel || dados?.rpi || ''}.csv`,
              ['Estado', ...cats.map(c => ROTULO_GATILHO[c.key] || c.titulo), 'Total', '% sem procurador'],
              linhas.map(l => [l.uf, ...cats.map(c => l[c.key] || 0), l.total, desass[l.uf] && desass[l.uf].total ? Math.round((desass[l.uf].sem / desass[l.uf].total) * 100) + '%' : '']))}
              disabled={!dados || !!dados?.erro}
              style={{ padding: '4px 10px', fontSize: 12, fontWeight: 600, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>
              📥 Excel
            </button>
          </div>
        </div>

        {!dados ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: 24, fontSize: 13 }}>Cruzando a revista…</div>
        ) : dados.erro ? (
          <div style={{ textAlign: 'center', color: '#dc2626', padding: 24, fontSize: 13 }}>⚠ {String(dados.erro)}</div>
        ) : (
          <>
            {/* Mapa do Brasil em temperatura */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ flex: '1 1 340px', maxWidth: 480 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gridTemplateRows: 'repeat(8, 1fr)', gap: 3 }}>
                  {Object.entries(UF_GRID).map(([uf, [col, lin]]) => {
                    const n = valorUf(uf);
                    const cor = corCalor(n, maxUf);
                    return (
                      <div key={uf} title={modo === 'pct'
                          ? `${UF_NOME[uf]}: ${n}% sem procurador (${desass[uf]?.sem || 0} de ${desass[uf]?.total || 0}) — clique para ver a lista`
                          : `${UF_NOME[uf]}: ${n.toLocaleString('pt-BR')} processo(s) — clique para ver a lista`}
                        onClick={() => setUfModal(uf)}
                        style={{ gridColumn: col + 1, gridRow: lin + 1, aspectRatio: '1', background: cor.bg, borderRadius: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.06)' }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: cor.texto, lineHeight: 1.1 }}>{uf}</div>
                        <div style={{ fontSize: 9.5, fontWeight: 600, color: cor.texto, opacity: 0.9 }}>{n ? (modo === 'pct' ? `${n}%` : n.toLocaleString('pt-BR')) : '·'}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 10, color: '#6b7280', flexWrap: 'wrap' }}>
                  frio
                  {['#fee2e2', '#fca5a5', '#ef4444', '#b91c1c', '#7f1d1d'].map(c => <span key={c} style={{ width: 18, height: 10, background: c, borderRadius: 2, display: 'inline-block' }} />)}
                  quente {semUf > 0 && <span style={{ marginLeft: 'auto' }}>· {semUf.toLocaleString('pt-BR')} sem UF informada</span>}
                </div>
              </div>
              <div style={{ flex: '0 1 220px', fontSize: 12, color: '#374151' }}>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{rotuloFiltro}</div>
                  {modo === 'pct' ? (
                    <>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#0a1628' }}>{pctBrasil}%</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>sem procurador no Brasil ({desTotais.sem.toLocaleString('pt-BR')} de {desTotais.total.toLocaleString('pt-BR')})</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#0a1628' }}>{totalFiltro.toLocaleString('pt-BR')}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>processos no filtro</div>
                    </>
                  )}
                  {Object.keys(UF_GRID).some(uf => valorUf(uf) > 0) && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>🔥 Mais quente: <strong>{Object.keys(UF_GRID).sort((a, b) => valorUf(b) - valorUf(a))[0]}</strong></div>}
                </div>
              </div>
            </div>

            {/* Planilha gatilhos × estado (igual ao dashboard do Radar) */}
            <div style={{ border: '1px solid #c8d0da', borderRadius: 4, overflow: 'auto', maxHeight: 360 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead><tr>
                  <th style={{ ...thD, textAlign: 'left' }}>Estado</th>
                  {cats.map(c => <th key={c.key} style={thD} title={c.titulo}>{ROTULO_GATILHO[c.key] || c.titulo}</th>)}
                  <th style={thD}>Total</th>
                </tr></thead>
                <tbody>
                  <tr style={{ background: '#f0f4ff' }}>
                    <td style={{ ...tdD, textAlign: 'left', fontWeight: 800, color: '#0a1628' }}>🇧🇷 Brasil</td>
                    {cats.map(c => <td key={c.key} style={{ ...tdD, fontWeight: 700 }}>{(totais[c.key] || 0).toLocaleString('pt-BR')}</td>)}
                    <td style={{ ...tdD, fontWeight: 800, color: '#0a1628' }}>{totalGeral.toLocaleString('pt-BR')}</td>
                  </tr>
                  {linhas.map((l, i) => (
                    <tr key={l.uf} style={{ background: i % 2 ? '#fafafa' : '#fff' }}>
                      <td style={{ ...tdD, textAlign: 'left', fontWeight: 700, color: '#0a1628' }} title={UF_NOME[l.uf] || 'UF não informada na revista'}>{l.uf} <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 10.5 }}>{UF_NOME[l.uf] || 'sem UF'}</span></td>
                      {cats.map(c => <td key={c.key} style={{ ...tdD, color: l[c.key] ? '#374151' : '#d1d5db', fontWeight: l[c.key] ? 600 : 400 }}>{l[c.key] ? l[c.key].toLocaleString('pt-BR') : '—'}</td>)}
                      <td style={{ ...tdD, fontWeight: 800, color: '#0369a1' }}>{l.total.toLocaleString('pt-BR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {ufModal && dados && !dados.erro && (
        <ModalProcessosUf uf={ufModal} rpi={rpiSel || dados.rpi}
          procurador={modo === 'pct' ? '__sem__' : procSel}
          despacho={despachoSel} gatilho={gatilhoSel} classe={classeSel}
          rotuloFiltro={modo === 'pct' ? `${rotuloFiltro} · 🚫 sem procurador` : rotuloFiltro}
          onFechar={() => setUfModal(null)} />
      )}
    </div>
  );
}


// ── Aba Evolução: curvas das últimas revistas + funil do INPI ────────────────
const ROTULO_METRICA = { total: 'Movimentações (7 gatilhos)', depositos: '📥 Depósitos publicados', concessao: '📜 Concessões de registro' };
function AbaEvolucao() {
  const [dados, setDados] = useState(null);
  const [n, setN] = useState(12);
  const [ufSel, setUfSel] = useState('');
  const [classeSel, setClasseSel] = useState('');
  const [metrica, setMetrica] = useState('total');

  useEffect(() => {
    setDados(null);
    const q = [`n=${n}`, ufSel ? `uf=${encodeURIComponent(ufSel)}` : '', classeSel ? `classe=${encodeURIComponent(classeSel)}` : ''].filter(Boolean).join('&');
    apiFetch(`marcas-rpi/dashboard-revista/evolucao?${q}`)
      .then(d => setDados(d.erro ? { erro: d.erro } : d))
      .catch(() => setDados({ erro: 'Falha ao montar a evolução' }));
  }, [n, ufSel, classeSel]);

  const serie = dados?.serie || [];
  const cats = dados?.categorias || [];
  const valor = (p) => p[metrica] || 0;
  const max = Math.max(1, ...serie.map(valor));
  const ultimo = serie[serie.length - 1];
  const anteriores = serie.slice(0, -1);
  const media = anteriores.length ? anteriores.reduce((s, p) => s + valor(p), 0) / anteriores.length : 0;
  const varPct = ultimo && media > 0 ? Math.round(((valor(ultimo) - media) / media) * 100) : null;
  const inpMini = { padding: '3px 6px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 12.5, background: '#fff', height: 26 };

  const FUNIL = [
    { key: 'depositos', rot: '📥 Depósitos publicados' },
    { key: 'exigencia', rot: '📩 Exigência de mérito' },
    { key: 'deferido', rot: '✅ Deferidos' },
    { key: 'indeferido', rot: '❌ Indeferidos' },
    { key: 'concessao', rot: '📜 Concessões' },
  ];
  const maxFunil = ultimo ? Math.max(1, ...FUNIL.map(f => ultimo[f.key] || 0)) : 1;

  return (
    <div style={{ background: '#fff', border: '1px solid #c8d0da', borderRadius: 4, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ background: '#e8edf2', borderLeft: '3px solid #0a1628', padding: '3px 10px' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0a1628', textTransform: 'uppercase', letterSpacing: '0.04em' }}>📈 Evolução do mercado</span>
        <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 10 }}>Tendência revista a revista — mercado aquecendo ou esfriando, por estado.</span>
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>Revistas</div>
            <select value={n} onChange={e => setN(parseInt(e.target.value))} style={inpMini}>
              <option value={12}>Últimas 12 (~3 meses)</option>
              <option value={26}>Últimas 26 (~6 meses)</option>
              <option value={52}>Últimas 52 (~1 ano)</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>Estado</div>
            <select value={ufSel} onChange={e => setUfSel(e.target.value)} style={inpMini}>
              <option value="">🇧🇷 Brasil inteiro</option>
              {Object.keys(UF_NOME).sort().map(uf => <option key={uf} value={uf}>{uf} — {UF_NOME[uf]}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 180 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>Classe NCL (segmento)</div>
            <SeletorClasse valor={classeSel} onChange={setClasseSel} estilo={{ ...inpMini, width: '100%' }} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>O que acompanhar</div>
            <select value={metrica} onChange={e => setMetrica(e.target.value)} style={{ ...inpMini, width: '100%' }}>
              <option value="total">{ROTULO_METRICA.total}</option>
              {cats.map(c => <option key={c.key} value={c.key}>{c.titulo}</option>)}
              <option value="depositos">{ROTULO_METRICA.depositos}</option>
              <option value="concessao">{ROTULO_METRICA.concessao}</option>
            </select>
          </div>
        </div>

        {!dados ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: 24, fontSize: 13 }}>Percorrendo as revistas… (quanto mais revistas, mais demora)</div>
        ) : dados.erro ? (
          <div style={{ textAlign: 'center', color: '#dc2626', padding: 24, fontSize: 13 }}>⚠ {String(dados.erro)}</div>
        ) : (
          <>
            {varPct != null && (
              <div style={{ marginBottom: 8, fontSize: 12, color: '#374151' }}>
                Última revista ({ultimo.rpi}): <strong>{valor(ultimo).toLocaleString('pt-BR')}</strong>
                <span style={{ marginLeft: 8, fontWeight: 700, color: varPct >= 0 ? '#15803d' : '#dc2626' }}>
                  {varPct >= 0 ? '▲' : '▼'} {Math.abs(varPct)}%
                </span>
                <span style={{ color: '#9ca3af' }}> vs média das {anteriores.length} anteriores ({Math.round(media).toLocaleString('pt-BR')})</span>
              </div>
            )}
            {/* Barras */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 140, borderBottom: '1px solid #c8d0da', padding: '0 2px' }}>
              {serie.map(p => (
                <div key={p.rpi} title={`RPI ${p.rpi}${p.data ? ` (${p.data})` : ''}: ${valor(p).toLocaleString('pt-BR')}`}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                  <div style={{ fontSize: 8.5, color: '#6b7280', textAlign: 'center', marginBottom: 1 }}>{serie.length <= 16 ? valor(p).toLocaleString('pt-BR') : ''}</div>
                  <div style={{ background: p === ultimo ? '#b91c1c' : '#0a1628', borderRadius: '2px 2px 0 0', height: `${Math.max(2, (valor(p) / max) * 100)}%` }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 2, padding: '2px 2px 0', marginBottom: 14 }}>
              {serie.map((p, i) => (
                <div key={p.rpi} style={{ flex: 1, fontSize: 8.5, color: '#9ca3af', textAlign: 'center' }}>
                  {(serie.length <= 16 || i % Math.ceil(serie.length / 16) === 0) ? p.rpi : ''}
                </div>
              ))}
            </div>

            {/* Funil da última revista */}
            {ultimo && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#0a1628', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  Funil do INPI — RPI {ultimo.rpi}{ufSel ? ` · ${ufSel}` : ' · Brasil'}
                </div>
                {FUNIL.map(f => {
                  const v = ultimo[f.key] || 0;
                  return (
                    <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <div style={{ width: 170, fontSize: 12, color: '#374151' }}>{f.rot}</div>
                      <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 3, height: 16 }}>
                        <div style={{ width: `${(v / maxFunil) * 100}%`, background: '#0a1628', height: '100%', borderRadius: 3, minWidth: v ? 2 : 0 }} />
                      </div>
                      <div style={{ width: 60, fontSize: 12, fontWeight: 700, color: '#0a1628', textAlign: 'right' }}>{v.toLocaleString('pt-BR')}</div>
                    </div>
                  );
                })}
                <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 4 }}>
                  Depósito de hoje é cliente de acompanhamento em 12–18 meses — estado com muitos depósitos agora é mercado pra plantar relacionamento.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Aba Concorrência: quem domina cada estado e quem está crescendo ─────────
const SOU_EU = /PRESERVA|VAGNER.*FREITAS/i;
function AbaConcorrencia() {
  const [dados, setDados] = useState(null);
  const [rpiSel, setRpiSel] = useState('');
  const [ufSel, setUfSel] = useState('PB');
  // Dimensão igual à aba Mapa: '' = tudo · 'g:key' = gatilho · 'd:COD' = despacho
  const [dimSel, setDimSel] = useState('');
  const despachoSel = dimSel.startsWith('d:') ? dimSel.slice(2) : '';
  const gatilhoSel = dimSel.startsWith('g:') ? dimSel.slice(2) : '';
  const inpMini = { padding: '3px 6px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 12.5, background: '#fff', height: 26 };
  const thD = { padding: '4px 8px', fontSize: 10.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', background: '#0a1628', whiteSpace: 'nowrap', textAlign: 'center' };
  const tdD = { padding: '3px 8px', fontSize: 12, color: '#374151', borderBottom: '1px solid #e8eaed', textAlign: 'center', whiteSpace: 'nowrap' };

  useEffect(() => {
    setDados(null);
    const q = [
      rpiSel ? `rpi=${encodeURIComponent(rpiSel)}` : '',
      `uf=${encodeURIComponent(ufSel)}`,
      despachoSel ? `despacho=${encodeURIComponent(despachoSel)}` : '',
      gatilhoSel ? `gatilho=${encodeURIComponent(gatilhoSel)}` : '',
    ].filter(Boolean).join('&');
    apiFetch(`marcas-rpi/dashboard-revista/concorrencia?${q}`)
      .then(d => setDados(d.erro ? { erro: d.erro } : d))
      .catch(() => setDados({ erro: 'Falha ao montar o ranking' }));
  }, [rpiSel, ufSel, despachoSel, gatilhoSel]);

  const esc = dados?.escritorios || [];
  return (
    <div style={{ background: '#fff', border: '1px solid #c8d0da', borderRadius: 4, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ background: '#e8edf2', borderLeft: '3px solid #0a1628', padding: '3px 10px' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0a1628', textTransform: 'uppercase', letterSpacing: '0.04em' }}>🏢 Concorrência por estado</span>
        <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 10 }}>Quem atende cada estado, quanto fica sem ninguém e quem está crescendo.</span>
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>RPI</div>
            <select value={rpiSel || dados?.rpi || ''} onChange={e => setRpiSel(e.target.value)} style={{ ...inpMini, minWidth: 150, fontWeight: 600 }}>
              {(dados?.rpis || (dados?.rpi ? [{ numero: dados.rpi }] : [])).map(r => (
                <option key={r.numero} value={r.numero}>RPI {r.numero}{r.data ? ` — ${r.data}` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>Estado</div>
            <select value={ufSel} onChange={e => setUfSel(e.target.value)} style={inpMini}>
              {Object.keys(UF_NOME).sort().map(uf => <option key={uf} value={uf}>{uf} — {UF_NOME[uf]}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>Despacho / Gatilho</div>
            <select value={dimSel} onChange={e => setDimSel(e.target.value)} style={{ ...inpMini, width: '100%' }}>
              <option value="">Todos os despachos</option>
              <optgroup label="🎯 Gatilhos comerciais (agrupados)">
                {(dados?.categorias || []).map(c => <option key={c.key} value={'g:' + c.key}>{c.titulo}</option>)}
              </optgroup>
              <optgroup label={`📜 Despachos do estado (${(dados?.despachos || []).length})`}>
                {(dados?.despachos || []).map(d => (
                  <option key={d.codigo} value={'d:' + d.codigo}>
                    {d.codigo} — {d.nome.length > 45 ? d.nome.slice(0, 45) + '…' : d.nome} ({d.n.toLocaleString('pt-BR')})
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          {dados && !dados.erro && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'flex-end', fontSize: 12, color: '#374151' }}>
              <span>Processos no filtro: <strong>{dados.total.toLocaleString('pt-BR')}</strong></span>
              <span style={{ color: '#b91c1c' }}>🚫 Sem procurador: <strong>{dados.sem.toLocaleString('pt-BR')}</strong>{dados.total ? ` (${Math.round((dados.sem / dados.total) * 100)}%)` : ''}</span>
            </div>
          )}
        </div>
        {!dados ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: 24, fontSize: 13 }}>Montando o ranking…</div>
        ) : dados.erro ? (
          <div style={{ textAlign: 'center', color: '#dc2626', padding: 24, fontSize: 13 }}>⚠ {String(dados.erro)}</div>
        ) : (
          <div style={{ border: '1px solid #c8d0da', borderRadius: 4, overflow: 'auto', maxHeight: 420 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead><tr>
                <th style={{ ...thD, textAlign: 'left' }}>#</th>
                <th style={{ ...thD, textAlign: 'left' }}>Escritório / Procurador</th>
                <th style={thD}>Processos</th>
                <th style={thD}>% do estado</th>
                <th style={thD} title={`Média das ${dados.comparadas} revistas anteriores`}>Média {dados.comparadas} ant.</th>
                <th style={thD}>Tendência</th>
              </tr></thead>
              <tbody>
                <tr style={{ background: '#fef2f2' }}>
                  <td style={tdD}>—</td>
                  <td style={{ ...tdD, textAlign: 'left', fontWeight: 700, color: '#b91c1c' }}>🚫 Sem procurador (mercado aberto)</td>
                  <td style={{ ...tdD, fontWeight: 700 }}>{dados.sem.toLocaleString('pt-BR')}</td>
                  <td style={tdD}>{dados.total ? Math.round((dados.sem / dados.total) * 100) : 0}%</td>
                  <td style={tdD}>—</td><td style={tdD}>—</td>
                </tr>
                {esc.map((e, i) => {
                  const meu = SOU_EU.test(e.nome);
                  return (
                    <tr key={e.nome} style={{ background: meu ? '#f0fdf4' : i % 2 ? '#fafafa' : '#fff' }}>
                      <td style={{ ...tdD, color: '#9ca3af' }}>{i + 1}º</td>
                      <td style={{ ...tdD, textAlign: 'left', fontWeight: meu ? 800 : 600, color: meu ? '#15803d' : '#0a1628' }}>
                        {meu && '🎯 '}{e.nome}{meu && <span style={{ fontWeight: 400, color: '#6b7280' }}> (você)</span>}
                      </td>
                      <td style={{ ...tdD, fontWeight: 700 }}>{e.n.toLocaleString('pt-BR')}</td>
                      <td style={tdD}>{Math.round(e.pct * 100)}%</td>
                      <td style={tdD}>{e.media.toLocaleString('pt-BR')}</td>
                      <td style={{ ...tdD, fontWeight: 700, color: e.delta > 0 ? '#15803d' : e.delta < 0 ? '#dc2626' : '#9ca3af' }}>
                        {e.delta > 0 ? `▲ +${e.delta}` : e.delta < 0 ? `▼ ${e.delta}` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Aba Preserva: share próprio e penetração da carteira, por estado ────────
function AbaPreserva() {
  const [dados, setDados] = useState(null);
  const [rpiSel, setRpiSel] = useState('');
  const inpMini = { padding: '3px 6px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 12.5, background: '#fff', height: 26 };
  const thD = { padding: '4px 8px', fontSize: 10.5, fontWeight: 700, color: '#fff', textTransform: 'uppercase', background: '#0a1628', whiteSpace: 'nowrap', textAlign: 'center' };
  const tdD = { padding: '3px 8px', fontSize: 12, color: '#374151', borderBottom: '1px solid #e8eaed', textAlign: 'center', whiteSpace: 'nowrap' };

  useEffect(() => {
    setDados(null);
    apiFetch(`marcas-rpi/dashboard-revista/preserva${rpiSel ? `?rpi=${encodeURIComponent(rpiSel)}` : ''}`)
      .then(d => setDados(d.erro ? { erro: d.erro } : d))
      .catch(() => setDados({ erro: 'Falha ao calcular o share' }));
  }, [rpiSel]);

  const linhas = (dados?.linhas || []).filter(l => l.uf !== '—').slice(0, 20);
  const Card = ({ rotulo, valor, sub, cor }) => (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 11, color: '#6b7280' }}>{rotulo}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: cor || '#0a1628' }}>{valor}</div>
      {sub && <div style={{ fontSize: 10.5, color: '#9ca3af' }}>{sub}</div>}
    </div>
  );
  return (
    <div style={{ background: '#fff', border: '1px solid #c8d0da', borderRadius: 4, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ background: '#e8edf2', borderLeft: '3px solid #0a1628', padding: '3px 10px' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0a1628', textTransform: 'uppercase', letterSpacing: '0.04em' }}>🎯 Share da Preserva</span>
        <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 10 }}>Sua fatia da revista (carteira + processos onde você é o procurador) e onde há mercado sem presença.</span>
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>RPI</div>
            <select value={rpiSel || dados?.rpi || ''} onChange={e => setRpiSel(e.target.value)} style={{ ...inpMini, minWidth: 150, fontWeight: 600 }}>
              {(dados?.rpis || (dados?.rpi ? [{ numero: dados.rpi }] : [])).map(r => (
                <option key={r.numero} value={r.numero}>RPI {r.numero}{r.data ? ` — ${r.data}` : ''}</option>
              ))}
            </select>
          </div>
        </div>
        {!dados ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: 24, fontSize: 13 }}>Cruzando a carteira com a revista…</div>
        ) : dados.erro ? (
          <div style={{ textAlign: 'center', color: '#dc2626', padding: 24, fontSize: 13 }}>⚠ {String(dados.erro)}</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <Card rotulo="Mercado (revista)" valor={dados.totalMercado.toLocaleString('pt-BR')} sub="processos publicados" />
              <Card rotulo="Preserva na revista" valor={dados.totalPreserva.toLocaleString('pt-BR')} sub="carteira + você como procurador" cor="#15803d" />
              <Card rotulo="Seu share" valor={`${(dados.share * 100).toFixed(2)}%`} cor="#15803d" sub="da revista inteira" />
              <Card rotulo="Carteira" valor={dados.processosCarteira.toLocaleString('pt-BR')} sub={`processos de ${dados.clientes} clientes`} />
            </div>
            <div style={{ border: '1px solid #c8d0da', borderRadius: 4, overflow: 'auto', maxHeight: 380 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead><tr>
                  <th style={{ ...thD, textAlign: 'left' }}>Estado</th>
                  <th style={thD}>Mercado (RPI)</th>
                  <th style={thD}>Preserva (RPI)</th>
                  <th style={thD}>Share</th>
                  <th style={thD} title="Processos de clientes seus nesse estado, na base toda">Carteira</th>
                  <th style={{ ...thD, textAlign: 'left' }}>Leitura</th>
                </tr></thead>
                <tbody>
                  {linhas.map((l, i) => {
                    const oportunidade = l.mercado >= 100 && !l.preserva && !l.carteira;
                    const presente = l.preserva > 0 || l.carteira > 0;
                    return (
                      <tr key={l.uf} style={{ background: oportunidade ? '#fefce8' : i % 2 ? '#fafafa' : '#fff' }}>
                        <td style={{ ...tdD, textAlign: 'left', fontWeight: 700, color: '#0a1628' }}>{l.uf} <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 10.5 }}>{UF_NOME[l.uf] || ''}</span></td>
                        <td style={{ ...tdD, fontWeight: 700 }}>{l.mercado.toLocaleString('pt-BR')}</td>
                        <td style={{ ...tdD, fontWeight: 700, color: l.preserva ? '#15803d' : '#d1d5db' }}>{l.preserva ? l.preserva.toLocaleString('pt-BR') : '—'}</td>
                        <td style={tdD}>{l.share ? `${(l.share * 100).toFixed(1)}%` : '—'}</td>
                        <td style={{ ...tdD, color: l.carteira ? '#374151' : '#d1d5db' }}>{l.carteira ? l.carteira.toLocaleString('pt-BR') : '—'}</td>
                        <td style={{ ...tdD, textAlign: 'left', fontSize: 11 }}>
                          {oportunidade ? '💡 Mercado grande sem presença — expansão'
                            : presente ? '✓ Presente — defender e crescer'
                            : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const ABAS = [
  { key: 'mapa', rotulo: '🗺️ Mapa da Revista' },
  { key: 'evolucao', rotulo: '📈 Evolução' },
  { key: 'concorrencia', rotulo: '🏢 Concorrência' },
  { key: 'preserva', rotulo: '🎯 Preserva' },
];

export default function DashboardRevistaPage() {
  const [aba, setAba] = useState('mapa');
  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ marginBottom: 10 }}>
        <h1 style={{ fontSize: 17, fontWeight: 700, color: '#0a1628' }}>📊 Dashboard da Revista</h1>
        <p style={{ color: '#6b7280', fontSize: 12, marginTop: 1 }}>Inteligência de mercado sobre as revistas do INPI — mapa, tendência, concorrência e o seu share.</p>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
        {ABAS.map(a => (
          <button key={a.key} onClick={() => setAba(a.key)}
            style={{
              padding: '5px 14px', fontSize: 12.5, fontWeight: 600, borderRadius: 4, cursor: 'pointer',
              border: `1px solid ${aba === a.key ? '#0a1628' : '#c8d0da'}`,
              background: aba === a.key ? '#0a1628' : '#fff', color: aba === a.key ? '#fff' : '#374151',
            }}>
            {a.rotulo}
          </button>
        ))}
      </div>
      {aba === 'mapa' && <ConteudoDashboard />}
      {aba === 'evolucao' && <AbaEvolucao />}
      {aba === 'concorrencia' && <AbaConcorrencia />}
      {aba === 'preserva' && <AbaPreserva />}
    </div>
  );
}
